"use client";
import React, { useEffect, useRef, useState } from "react";

export type AnimatedHeroGraphicProps = {
  className?: string;
  width?: number; // default: 583
  height?: number; // default: 400
};

type CirclePosition = {
  x: number;
  y: number;
};

const AnimatedHeroGraphic: React.FC<AnimatedHeroGraphicProps> = ({
  className = "",
  width = 583,
  height = 400,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [circlePositions, setCirclePositions] = useState<{
    left: CirclePosition;
    right: CirclePosition;
  }>({
    left: { x: -150, y: 200 },
    right: { x: 733, y: 200 },
  });

  const dots = React.useMemo(() => {
    const pattern: Array<{ cx: number; cy: number; r: number }> = [];
    const startX = 1.9;
    const startY = 8.96;
    const spacingX = 19.955;
    const spacingY = 19.18;
    const radius = 1;

    for (let row = 0; row < 21; row++) {
      for (let col = 0; col < 30; col++) {
        pattern.push({
          cx: startX + col * spacingX,
          cy: startY + row * spacingY,
          r: radius,
        });
      }
    }

    return pattern;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Easing function for merge animation
    const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

    // Animation parameters
    const targetLeftX = 210;
    const targetRightX = 350;
    const startLeftX = -150;
    const startRightX = 733;
    const mergeDuration = 2000; // 2 seconds
    let mergeStartTime: number | null = null;

    // Draw animated mesh gradient
    const drawMeshGradient = (time: number): HTMLCanvasElement => {
      const offscreen = document.createElement("canvas");
      offscreen.width = width;
      offscreen.height = height;
      const offCtx = offscreen.getContext("2d");
      if (!offCtx) return offscreen;

      // Fill background
      offCtx.fillStyle = "#0F172A";
      offCtx.fillRect(0, 0, width, height);

      // Define gradient blobs with colors: #4271B7, #355BA9, #0AB3B8
      const blobs = [
        {
          baseX: 150,
          baseY: 120,
          color: "rgba(66, 113, 183, 0.8)", // #4271B7
          radiusX: 180,
          radiusY: 140,
          speedX: 0.0003,
          speedY: 0.0004,
          phaseX: 0,
          phaseY: 0.5,
        },
        {
          baseX: 400,
          baseY: 280,
          color: "rgba(53, 91, 169, 0.7)", // #355BA9
          radiusX: 160,
          radiusY: 180,
          speedX: 0.0005,
          speedY: 0.0003,
          phaseX: 1.2,
          phaseY: 0.8,
        },
        {
          baseX: 280,
          baseY: 200,
          color: "rgba(10, 179, 184, 0.6)", // #0AB3B8
          radiusX: 200,
          radiusY: 160,
          speedX: 0.0004,
          speedY: 0.0005,
          phaseX: 2.4,
          phaseY: 1.6,
        },
        {
          baseX: 450,
          baseY: 100,
          color: "rgba(66, 113, 183, 0.5)", // #4271B7
          radiusX: 140,
          radiusY: 120,
          speedX: 0.0006,
          speedY: 0.0004,
          phaseX: 3.6,
          phaseY: 2.4,
        },
        {
          baseX: 100,
          baseY: 300,
          color: "rgba(10, 179, 184, 0.5)", // #0AB3B8
          radiusX: 150,
          radiusY: 130,
          speedX: 0.0003,
          speedY: 0.0006,
          phaseX: 4.8,
          phaseY: 3.2,
        },
      ];

      // Draw each blob with animated position
      blobs.forEach((blob) => {
        const x =
          blob.baseX + Math.sin(time * blob.speedX + blob.phaseX) * 80;
        const y =
          blob.baseY + Math.cos(time * blob.speedY + blob.phaseY) * 60;

        const gradient = offCtx.createRadialGradient(
          x,
          y,
          0,
          x,
          y,
          blob.radiusX,
        );
        gradient.addColorStop(0, blob.color);
        gradient.addColorStop(1, "rgba(15, 23, 42, 0)");

        offCtx.fillStyle = gradient;
        offCtx.beginPath();
        offCtx.ellipse(x, y, blob.radiusX, blob.radiusY, 0, 0, Math.PI * 2);
        offCtx.fill();
      });

      return offscreen;
    };

    // Main animation loop
    const animate = (timestamp: number) => {
      if (!mergeStartTime) mergeStartTime = timestamp;
      const elapsed = timestamp - mergeStartTime;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw mesh gradient
      const meshCanvas = drawMeshGradient(timestamp);

      // Apply dot pattern as clipping mask
      ctx.save();
      ctx.beginPath();
      dots.forEach((dot) => {
        ctx.moveTo(dot.cx + dot.r, dot.cy);
        ctx.arc(dot.cx, dot.cy, dot.r, 0, Math.PI * 2);
      });
      ctx.clip();
      ctx.drawImage(meshCanvas, 0, 0);
      ctx.restore();

      // Calculate circle positions
      let leftX: number, rightX: number, floatY = 200;

      if (elapsed < mergeDuration) {
        // Merge animation phase
        const progress = easeOutCubic(elapsed / mergeDuration);
        leftX = startLeftX + (targetLeftX - startLeftX) * progress;
        rightX = startRightX + (targetRightX - startRightX) * progress;
      } else {
        // Floating animation phase - smooth transition from merge
        const floatTime = (timestamp - mergeStartTime - mergeDuration) / 1000;
        // Use a smooth easing function to transition into floating (prevents jerk)
        const floatEase = 1 - Math.exp(-floatTime * 2); // Exponential ease-in
        const floatOffsetX = Math.sin(floatTime * 0.5) * 8 * floatEase;
        const floatOffsetY = Math.cos(floatTime * 0.7) * 6 * floatEase;

        leftX = targetLeftX + floatOffsetX;
        rightX = targetRightX + floatOffsetX;
        floatY = 200 + floatOffsetY;
      }

      // Update circle positions
      setCirclePositions({
        left: { x: leftX, y: floatY },
        right: { x: rightX, y: floatY },
      });

      // Continue animation
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dots, width, height]);

  return (
    <div
      className={`relative ${className}`}
      role="img"
      aria-label="Animated hero graphic with mesh gradient and floating circles"
    >
      {/* Canvas layer - animated mesh gradient masked by dots */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="absolute top-0 left-0 w-full h-full"
        style={{ position: "absolute", top: 0, left: 0 }}
      />

      {/* SVG layer - animated merging/floating circles */}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        fill="none"
        className="absolute top-0 left-0 w-full h-full"
        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Right circle group (purple) - rendered first (bottom layer) */}
        <circle
          cx={circlePositions.right.x}
          cy={circlePositions.right.y}
          r={98}
          fill="#355BA9"
          opacity={0.35}
        />

        {/* Left circle group (teal) - rendered second (top layer) */}
        <circle
          cx={circlePositions.left.x}
          cy={circlePositions.left.y}
          r={98}
          fill="#0AB3B8"
          opacity={0.35}
        />
      </svg>
    </div>
  );
};

export default AnimatedHeroGraphic;

